/**
 * STT 끝점 — **호스트까지만** 받는다 (2026-09-21 · 관제 확인).
 *
 * ## ★★★ 왜 전체 URL 을 안 받나
 *
 * 붙은 서버는 **OpenAI 호환**이라 경로를 **규약이 정한다** — `POST /v1/audio/transcriptions`.
 * 전체 URL 을 받으면 운영자가 `/v1/models` 를 넣어도 **저장되고**, 틀린 것이 **저장 시점에 안 잡힌다.**
 * 그러면 「넣었는데 안 되네」가 되고, 그때는 키를 의심하게 된다(키는 멀쩡한데).
 *
 * ## ⛔ `https` 만 받으면 안 된다
 *
 * 실제 서버가 **`http://ms-ssh.tchain.cloud:9293`** 이다(사내 주소). `https` 를 강제하면 **이 서버를 못 넣는다.**
 * 「더 안전해 보이는 검사」가 **멀쩡한 값을 막는** 자리라 시험으로 못 박아 둔다.
 *
 * ## ⚠ 스킴을 생략하면 못 읽는다
 *
 * `new URL('ms-ssh.tchain.cloud:9293')` 은 오류가 아니라 **스킴 `ms-ssh:`** 로 읽힌다(호스트는 빈 값).
 * 그래서 **`http://`·`https://` 로 시작하는지 먼저 본다** — 그러지 않으면 엉뚱한 값이 조용히 통과한다.
 */

/** 규약이 정하는 경로 — 화면이 「이 뒤에 이것을 붙여 부릅니다」로 보여 준다. */
export const STT_PATH = '/v1/audio/transcriptions';

export type EndpointProblem = 'empty' | 'no_scheme' | 'bad_scheme' | 'not_a_url' | 'has_path';

export type EndpointCheck =
  | { ok: true; value: string }
  | { ok: false; problem: EndpointProblem; message: string };

const MESSAGE: Record<EndpointProblem, string> = {
  empty: '끝점을 적으십시오.',
  no_scheme: 'http:// 또는 https:// 로 시작하는 주소를 적으십시오.',
  bad_scheme: 'http:// 또는 https:// 만 됩니다.',
  not_a_url: '주소를 읽을 수 없습니다 — 예: http://ms-ssh.tchain.cloud:9293',
  has_path: `경로는 빼고 호스트까지만 적으십시오 — ${STT_PATH} 는 규약이 정하고 서버가 붙입니다.`,
};

const fail = (problem: EndpointProblem): EndpointCheck => ({ ok: false, problem, message: MESSAGE[problem] });

/** 적은 글자 → 저장할 끝점(`http://host:port`) 또는 왜 못 쓰는지. */
export function checkSttEndpoint(raw: string): EndpointCheck {
  const text = raw.trim();
  if (text === '') return fail('empty');
  // ★ 스킴 먼저 — 생략하면 URL 이 «오류 없이» 엉뚱하게 읽힌다(위 머리말).
  if (!/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(text)) return fail('no_scheme');
  let u: URL;
  try { u = new URL(text); } catch { return fail('not_a_url'); }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return fail('bad_scheme');
  if (u.hostname === '') return fail('not_a_url');
  // 경로 · 물음표 · 조각은 전부 「경로를 적었다」로 본다 — 규약이 정하는 자리다.
  if ((u.pathname !== '' && u.pathname !== '/') || u.search !== '' || u.hash !== '') return fail('has_path');
  return { ok: true, value: `${u.protocol}//${u.host}` };
}

/**
 * ⚠ 평문 경고 — **막지 않고 «말만» 한다** (2026-09-21 사용자 결정 · 관제 전달).
 *
 * 이 주소로 가는 것은 **회의 녹음 원음**이고 조각 하나가 5분이다. 평문 `http` 면 **같은 망을 지나는
 * 누구나 들을 수 있다.** 그런데 실제 서버가 사내 `http` 라 **막으면 쓸 수가 없다**
 * — 그래서 **서버는 막지 않되, 사람이 «모르고» 하지는 않게** 한다.
 *
 * ⛔ **이 경고는 저장을 막지 않는다.** 막는 순간 이 서버를 못 넣는다.
 * ⚠ `https` 에는 띄우지 않는다 — **늘 뜨는 경고는 아무도 안 읽는다.**
 */
export const PLAINTEXT_WARNING = '평문(http)입니다. 회의 녹음이 그대로 이 주소로 지나갑니다 — 사내망에서만 쓰십시오.';

/** 쓸 수 있는 끝점인데 평문일 때만 경고를 준다. 못 읽는 값이면 null — 그건 경고가 아니라 오류다. */
export function sttEndpointWarning(raw: string): string | null {
  const c = checkSttEndpoint(raw);
  if (!c.ok) return null;
  return c.value.startsWith('http://') ? PLAINTEXT_WARNING : null;
}

/** 화면에 보여 줄 「실제로 부를 주소」. 못 읽는 값이면 null(지어내지 않는다). */
export function sttCallUrl(raw: string): string | null {
  const c = checkSttEndpoint(raw);
  return c.ok ? c.value + STT_PATH : null;
}

/** 타임아웃 — 초. 비우면 기본값을 쓴다. */
export const STT_TIMEOUT_DEFAULT_SEC = 30;
export const STT_TIMEOUT_MAX_SEC = 600;

export type TimeoutCheck = { ok: true; value: number } | { ok: false; message: string };

export function checkSttTimeout(raw: string): TimeoutCheck {
  const text = raw.trim();
  if (text === '') return { ok: true, value: STT_TIMEOUT_DEFAULT_SEC };
  const n = Number(text);
  if (!Number.isInteger(n) || n < 1 || n > STT_TIMEOUT_MAX_SEC) {
    return { ok: false, message: `타임아웃은 1 ~ ${STT_TIMEOUT_MAX_SEC} 사이의 정수(초)로 적습니다.` };
  }
  return { ok: true, value: n };
}
